import {
  createCapitalGoods,
  createResidualWaste,
  createUtilityConsumable,
  deleteUtilityContract,
  getCapitalGoods,
  getCoolingOptions,
  getElectricityOptions,
  getFacility,
  getFacilityContracts,
  GetFacilityContracts,
  getFacilityOverviewYears,
  getFacilityYears,
  getHeatOptions,
  getPublishedIdDetail,
  getWaste,
  getWaterOptions,
  IFacility,
  insertContract,
  insertFacility,
  setUsed,
  updateCapitalGoods,
  updateContract,
  updateFacility,
  updateResidualWaste,
  updateUtilityConsumable,
  UtilityType,
  utilityTypes,
} from "@/api/facilities";
import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { dateFormat } from "@/pages/dashboard/hooks/useDateRange";
import { filterSearchToGetFilter, GetData, sortToParam } from "@/utilities/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import dayjs from "dayjs";
import useSmartQueryClient from "./useSmartQueryClient";

export function useGetFacilities({
  pagination,
  filter,
  search,
  sort,
  enabled,
  year,
  showUnrelated,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  enabled?: boolean;
  year?: number;
  showUnrelated?: boolean;
}) {
  return useQuery({
    queryKey: ["facilities", "list", year, showUnrelated, pagination, filter, search, sort],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GetData<IFacility>>("/facilities", {
          params: Object.fromEntries(
            Object.entries({
              year: year,
              page: pagination?.page === undefined ? 1 : pagination.page,
              page_size: pagination?.pageSize || 50,
              show_unrelated: showUnrelated,
              ...filterSearchToGetFilter(filter, search),
              ...sortToParam(sort),
            }).filter(([_, value]) => value !== undefined)
          ),
        })
        .then((result) => result.data),
  });
}

export function useGetFacility(facilityId: number, enabled?: boolean) {
  return useQuery({
    queryKey: ["facilities", "detail", facilityId],
    queryFn: () => getFacility(facilityId),
    enabled,
  });
}

export function useGetFacilityContracts(facilityId: number, enabled?: boolean) {
  return useQuery({
    queryKey: ["facilities", "contracts", facilityId],
    queryFn: () => getFacilityContracts(facilityId),
    enabled,
  });
}

export function useUpdateFacility() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationKey: ["facilities", "put"],
    mutationFn: updateFacility,
    onSuccess: (_, variables) => {
      smartQueryClient.invalidateQueries(["facilities"]);
    },
  });
}

export function useInsertFacility() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationKey: ["facilities", "post"],
    mutationFn: insertFacility,
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["facilities"]);
    },
  });
}

export function useUpdateContract() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationKey: ["contracts", "put"],
    mutationFn: updateContract,
    onSuccess: (data, variables) => {
      smartQueryClient.invalidateQueries(["facilities", "detail", variables.contract.facility_id]);
      smartQueryClient.invalidateQueries([
        "facilities",
        "contracts",
        variables.contract.facility_id,
      ]);
      smartQueryClient.invalidateQueries(["facilities", "list"]);
    },
  });
}

export function useUpdateFacilityUsed() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationKey: ["facilities", "setUsed"],
    mutationFn: setUsed,
    onSuccess: (_, variables) => {
      smartQueryClient.invalidateQueries(["facilities", "detail", variables.facilityId]);
      smartQueryClient.invalidateQueries(["facilities", "list"]);
    },
    onMutate: (variables) => {
      smartQueryClient.setQueryData<GetFacilityContracts>(
        ["facilities", "contracts", variables.facilityId],
        (queryData) => {
          if (!queryData) return undefined;
          const newQueryData = { ...queryData };
          if (variables.setUsed.utility_type) {
            newQueryData.contracts[variables.setUsed.year][variables.setUsed.utility_type].used =
              variables.setUsed.used;
            newQueryData.contracts[variables.setUsed.year][variables.setUsed.utility_type].status =
              variables.setUsed.used ? "incomplete" : "complete";
          } else {
            utilityTypes.forEach((type: UtilityType) => {
              newQueryData.contracts[variables.setUsed.year][type].used = variables.setUsed.used;
              newQueryData.contracts[variables.setUsed.year][type].status = variables.setUsed.used
                ? "incomplete"
                : "complete";
            });
          }

          return newQueryData;
        }
      );
    },
  });
}

export function useInsertContract() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationKey: ["contracts", "post"],
    mutationFn: insertContract,
    onSuccess: (_, variables) => {
      smartQueryClient.invalidateQueries(["facilities", "detail", variables.contract.facility_id]);
      smartQueryClient.invalidateQueries([
        "facilities",
        "contracts",
        variables.contract.facility_id,
      ]);
      smartQueryClient.invalidateQueries(["facilities", "list"]);
    },
  });
}

export function useDeleteContract() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationKey: ["contracts", "post"],
    mutationFn: deleteUtilityContract,
    onSuccess: (_, { utilityId }) => {
      smartQueryClient.invalidateQueries(["facilities", "list"]);
      smartQueryClient.invalidateQueries(["facilities", "detail", utilityId]);
      smartQueryClient.invalidateQueries(["facilities", "contracts", utilityId]);
    },
  });
}

export function useGetElectricityOptions({
  facilityId,
  enabled,
}: {
  facilityId: number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["facilities", "utilityOptions", "electricity", facilityId],
    queryFn: () => getElectricityOptions({ facilityId }),
    retry: 0,
    enabled,
  });
}

export function useDeleteFacilities() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ facilityIds, deleteAll }: { facilityIds: number[]; deleteAll?: boolean }) =>
      axios.delete<{ deleted_ids: number[] }>("/facilities", {
        params: deleteAll
          ? {}
          : {
              ids: facilityIds.join(";"),
            },
      }),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["facilities"]);
    },
  });
}

export function useGetHeatOptions({
  facilityId,
  enabled,
}: {
  facilityId: number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["heatOptions", facilityId],
    queryFn: () => getHeatOptions({ facilityId }),
    retry: 0,
    enabled,
  });
}

export const getFacilityProductMass = async ({
  facilityId,
  startDate,
  endDate,
}: {
  facilityId: number;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
}) => {
  const { data } = await axios.get<{ product_mass: number }>(
    `/facilities/${facilityId}/product_mass?start_date=${startDate.format(
      dateFormat
    )}&end_date=${endDate.format(dateFormat)}`
  );
  return data;
};

export function useGetFacilityProductMass({
  facilityId,
  startDate,
  endDate,
  enabled,
}: {
  facilityId: number;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["facilities", facilityId, "product_mass", startDate, endDate],
    enabled: enabled,
    queryFn: () => getFacilityProductMass({ facilityId, startDate, endDate }),
  });
}

export function useCreateResidualWaste() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationKey: ["residual_waste", "create"],
    mutationFn: createResidualWaste,
    onSuccess: (data, variables) => {
      smartQueryClient.invalidateQueries(["facilities", "detail", variables.contract.facility_id]);
      smartQueryClient.invalidateQueries([
        "facilities",
        "contracts",
        variables.contract.facility_id,
      ]);
      smartQueryClient.invalidateQueries(["facilities", "list"]);
    },
  });
}

export function useUpdateResidualWaste() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationKey: ["residual_waste", "update"],
    mutationFn: updateResidualWaste,
    onSuccess: (data, variables) => {
      smartQueryClient.invalidateQueries(["facilities", "detail", variables.contract.facility_id]);
      smartQueryClient.invalidateQueries([
        "facilities",
        "contracts",
        variables.contract.facility_id,
      ]);
      smartQueryClient.invalidateQueries(["facilities", "contracts", variables.contractId]);
      smartQueryClient.invalidateQueries(["facilities", "list"]);
    },
  });
}

export function useGetResidualWaste(contractId: number, enabled?: boolean) {
  return useQuery({
    queryKey: ["facilities", "contracts", contractId],
    queryFn: () => getWaste(contractId),
    enabled,
  });
}

export function useGetCoolingOptions(facilityId: number, enabled?: boolean) {
  return useQuery({
    queryKey: ["facilities", "utilityOptions", "cooling", facilityId],
    queryFn: () => getCoolingOptions({ facilityId }),
    enabled,
  });
}

export function useCreateCapitalGoods() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationKey: ["capital_goods", "create"],
    mutationFn: createCapitalGoods,
    onSuccess: (data, variables) => {
      smartQueryClient.invalidateQueries(["facilities", "detail", variables.contract.facility_id]);
      smartQueryClient.invalidateQueries([
        "facilities",
        "contracts",
        variables.contract.facility_id,
      ]);
      smartQueryClient.invalidateQueries(["facilities", "list"]);
    },
  });
}

export function useUpdateCapitalGoods() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationKey: ["capital_goods", "update"],
    mutationFn: updateCapitalGoods,
    onSuccess: (data, variables) => {
      smartQueryClient.invalidateQueries(["facilities", "detail", variables.contract.facility_id]);
      smartQueryClient.invalidateQueries([
        "facilities",
        "contracts",
        variables.contract.facility_id,
      ]);
      smartQueryClient.invalidateQueries(["facilities", "contracts", variables.contractId]);
      smartQueryClient.invalidateQueries(["facilities", "list"]);
    },
  });
}

export function useGetCapitalGoods(contractId: number, enabled?: boolean) {
  return useQuery({
    queryKey: ["facilities", "contracts", contractId],
    queryFn: () => getCapitalGoods(contractId),
    enabled,
  });
}

export function useGetWaterOptions(facilityId: number, enabled?: boolean) {
  return useQuery({
    queryKey: ["facilities", "utilityOptions", "water", facilityId],
    queryFn: () => getWaterOptions({ facilityId }),
    enabled,
  });
}

export function useCreateUtilityConsumable() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationKey: ["utility_consumables", "create"],
    mutationFn: createUtilityConsumable,
    onSuccess: (data, variables) => {
      smartQueryClient.invalidateQueries(["consumables"]);
      smartQueryClient.invalidateQueries(["facilities", "detail", variables.contract.facility_id]);
      smartQueryClient.invalidateQueries([
        "facilities",
        "contracts",
        variables.contract.facility_id,
      ]);
      smartQueryClient.invalidateQueries(["facilities", "list"]);
    },
  });
}

export function useUpdateUtilityConsumable() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationKey: ["utility_consumables", "update"],
    mutationFn: updateUtilityConsumable,
    onSuccess: (data, variables) => {
      smartQueryClient.invalidateQueries(["consumables"]);
      smartQueryClient.invalidateQueries(["facilities", "detail", variables.contract.facility_id]);
      smartQueryClient.invalidateQueries([
        "facilities",
        "contracts",
        variables.contract.facility_id,
      ]);
      smartQueryClient.invalidateQueries(["facilities", "contracts", variables.contractId]);
      smartQueryClient.invalidateQueries(["facilities", "list"]);
    },
  });
}

export function useGetFacilityYears({ facilityId }: { facilityId: number }) {
  return useQuery({
    queryKey: ["facilities", facilityId, "years"],
    queryFn: () => getFacilityYears({ facilityId }),
  });
}

export function useGetFacilityOverviewYears() {
  return useQuery({
    queryKey: ["facilities", "list", "years"],
    queryFn: () => getFacilityOverviewYears(),
  });
}

export function useBulkPostUsedAllUtilities() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({
      facilityIds,
      year,
      postUsed,
    }: {
      facilityIds: number[];
      year: number;
      postUsed: Record<string, boolean>;
    }) =>
      axios.post<{ deleted_ids: number[] }>(
        `/facilities/bulk_used?facility_ids=${facilityIds.join(";")}&year=${year}`,
        postUsed
      ),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["facilities"]);
    },
  });
}

export function usePublishFacility() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ facilityId }: { facilityId: number }) =>
      axios.post<{ published_id: number }>(`/facilities/${facilityId}/publish`),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["facilities"]);
    },
  });
}

export function useUnPublishFacility() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ facilityId }: { facilityId: number }) =>
      axios.post<{ published_id: number }>(`/facilities/${facilityId}/unpublish`),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["facilities"]);
    },
  });
}

export function useGetPublishedIdDetail(enabled: boolean) {
  return useQuery({
    queryKey: ["published", "detail"],
    queryFn: () => getPublishedIdDetail(),
    enabled,
  });
}
